import PropTypes from 'prop-types';
import { deletedAtFromUrl, isDeletedImageUrl } from '@/files/deleted';

export { isDeletedImageUrl };

function formatDate(ms) {
    if (!ms) return '';
    try {
        return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    } catch {
        return '';
    }
}

/**
 * Stands in for an image that was removed from the Bin for good: a grey box
 * at the image's ratio with the faded tombstone thumbnail, if there is one.
 */
export default function DeletedImagePlaceholder({
    thumbnailUrl = null,
    deletedAt = null,
    width = null,
    height = null,
    fill = false,
    label = 'Removed from the Bin',
    className = '',
    style,
}) {
    const date = formatDate(deletedAt || deletedAtFromUrl(thumbnailUrl));
    const ratio = width && height ? `${width} / ${height}` : '3 / 2';
    const thumb = thumbnailUrl ? `/${String(thumbnailUrl).replace(/^\/+/, '')}` : null;
    const box = fill
        ? { position: 'absolute', inset: 0 }
        : { position: 'relative', width: '100%', aspectRatio: ratio };
    return (
        <div
            className={className}
            data-deleted-image
            role="img"
            aria-label={label}
            style={{
                ...box,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                background: '#e5e5e5',
                color: '#555',
                fontFamily: 'system-ui, sans-serif',
                fontSize: 12,
                lineHeight: 1.3,
                textAlign: 'center',
                ...(style || {}),
                ...(fill ? { position: 'absolute', inset: 0 } : null),
            }}
        >
            {thumb && (
                <span
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: `url(${JSON.stringify(thumb)})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        opacity: 0.25,
                        filter: 'grayscale(1)',
                    }}
                />
            )}
            <span style={{ position: 'relative', padding: 8 }}>
                {label}
                {date && <span style={{ display: 'block', opacity: 0.7 }}>{date}</span>}
            </span>
        </div>
    );
}

DeletedImagePlaceholder.propTypes = {
    thumbnailUrl: PropTypes.string,
    deletedAt: PropTypes.number,
    width: PropTypes.number,
    height: PropTypes.number,
    fill: PropTypes.bool,
    label: PropTypes.string,
    className: PropTypes.string,
    style: PropTypes.object,
};
